import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Route as RouteIcon } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { RoutingSuggestionRow } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Badge, { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface RoutingData {
  data: RoutingSuggestionRow[]
  warehouses: { id: number; name: string; daily_capacity: number; contact_person?: string; phone?: string }[]
}

export default function AdminRouting() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['admin-routing'],
    queryFn: async () => (await api.get<RoutingData>('/admin/routing')).data,
  })

  const act = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'approve' | 'override' }) =>
      (await api.post(`/admin/routing/${id}/${action}`)).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-routing'] })
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['admin-dash'] })
      toast.success(res.message)
    },
    onError: (err) => toast.error('Action failed', errorMessage(err)),
  })

  if (isLoading || !data) return <LogoLoader />

  return (
    <div>
      <PageHeader
        title="External warehouse routing"
        subtitle="When in-house capacity overflows, the AI analyzer suggests external production for the lowest-profit orders"
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-2">
        {data.warehouses.map((wh) => (
          <Card key={wh.id}>
            <div className="flex items-center gap-3">
              <span className="flex size-10 items-center justify-center rounded-full border border-primary/60 bg-primary/20 text-primary">
                <Building2 className="size-5" />
              </span>
              <div>
                <p className="font-bold">{wh.name}</p>
                <p className="text-xs text-muted">
                  Capacity {wh.daily_capacity} pcs/day - {wh.contact_person} ({wh.phone})
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <SectionLabel>Routing suggestions (AI analyzer - decided by projected profit, not tier)</SectionLabel>
      <Table columns={['Order', 'Customer', 'Profit score', 'AI reasoning', 'Suggested warehouse', 'Status', actionsColumn]}>
        {data.data.length === 0 && (
          <EmptyRow colSpan={7}>
            <span className="inline-flex items-center gap-2">
              <RouteIcon className="size-4" /> No routing suggestions - in-house capacity is holding up.
            </span>
          </EmptyRow>
        )}
        {data.data.map((row) => (
          <Row key={row.id}>
            <Cell className="font-semibold">{row.order?.number}</Cell>
            <Cell>
              <p>{row.order?.user?.name}</p>
              {row.order?.user?.tier && <Badge tone="brand">{row.order.user.tier.name}</Badge>}
            </Cell>
            <Cell>
              <span className="font-mono font-bold">{row.score}</span>
              <span className="block text-2xs text-muted">lower profit = external</span>
            </Cell>
            <Cell className="max-w-56 text-xs">{row.reason}</Cell>
            <Cell>{row.warehouse?.name}</Cell>
            <Cell>
              <StateBadge state={row.status} />
            </Cell>
            <Cell className={actionsCellClass}>
              {row.status === 'suggested' && (
                <div className="flex gap-2">
                  <Button
                    loading={act.isPending && act.variables?.id === row.id && act.variables.action === 'approve'}
                    onClick={() => act.mutate({ id: row.id, action: 'approve' })}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    loading={act.isPending && act.variables?.id === row.id && act.variables.action === 'override'}
                    onClick={() => act.mutate({ id: row.id, action: 'override' })}
                  >
                    Keep in-house
                  </Button>
                </div>
              )}
            </Cell>
          </Row>
        ))}
      </Table>
    </div>
  )
}
